import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import DrawFreehandIcon from '@src/svgs/DrawFreehandIcon';
import EmojiIcon from '@src/svgs/EmojiIcon';
import SendIcon from '@src/svgs/SendIcon';
import { DRAWING_PAYLOAD_VERSION } from '@src/utility/commentDrawing';
import {
  encodeMentionTokens,
  splitDisplayForRender,
} from '@src/utility/commentMentions';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import { observer } from 'mobx-react-lite';
import React, { useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { COMPACT_TOOLTIP_STYLE } from '../../common/tooltipStyles';
import { AnnotationControls } from '../annotations/AnnotationControls';
import { EMOJI_PICKER_MENU_WIDTH, EmojiPickerMenu } from './EmojiPickerMenu';
import { MentionSuggestions } from './MentionSuggestions';
import { useMentionAutocomplete } from './useMentionAutocomplete';

import type { ClipAnnotations } from '../annotations/useClipAnnotations';
import type { MentionPerson } from './useMentionAutocomplete';
import type { PlaybackBridge } from '@src/hooks/PlaybackBridge';
import type { CommentStore } from '@src/stores-v2/CommentStore';
import type { Drawing } from '@src/types.ts/comment';

interface CommentComposerProps {
  store: CommentStore;
  playback: PlaybackBridge;
  /** Workspace teammates offered by the @-mention autocomplete. */
  mentionPeople?: MentionPerson[];
  /**
   * Collision-free avatar colors by person name, built once by the owning modal
   * so every comment surface (list, markers, this popover) agrees on a hue.
   * Names outside the map fall back to the label hash.
   */
  personColors?: Map<string, string>;
  /**
   * Drawing controller from the owning modal (PLAT-11289). Omitted when the
   * comment-drawing flag is off, which is also what hides the tools row — the
   * composer has no flag knowledge of its own.
   */
  annotations?: ClipAnnotations;
}

/**
 * Composer for posting a new top-level comment, pinned to the playhead timecode
 * (or general). The chip tracks the live playhead and the timecode is captured
 * at post time, so the comment lands on wherever the video is when you post
 * (PLAT-10301). Replies and the thread list live in later tickets; this only
 * posts through `CommentStore.addComment`.
 */
export const CommentComposer: React.FC<CommentComposerProps> = observer(
  ({ store, playback, mentionPeople = [], personColors, annotations }) => {
    const videoCreator = useVideoCreatorStore();
    const [body, setBody] = useState('');
    const [isPosting, setIsPosting] = useState(false);
    // Drawing tools are revealed by the toggle below, not shown by default — the
    // composer stays a text box until the reviewer asks to draw.
    const [drawingOpen, setDrawingOpen] = useState(false);
    // Whether the comment pins to the playhead. The pinned time tracks the
    // live player position (`seekTime` while dragging, else `currentTime`) and
    // is captured at post time.
    const [pinned, setPinned] = useState(true);

    // Live playhead: the scrub target while dragging, else the current time.
    const playheadTime = () => playback.seekTime ?? playback.currentTime;

    // Shared by the mention autocomplete (caret position for the popover) and
    // the emoji inserter (caret position for the insertion point).
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // @-mention autocomplete over the showcase's assignable members.
    // Scroll-synced mirror that tints display mentions gold (see InputShell).
    const highlightRef = useRef<HTMLDivElement>(null);
    const mention = useMentionAutocomplete(
      body,
      setBody,
      textareaRef,
      mentionPeople,
    );

    // Auto-grow with the draft (up to the CSS max-height, then scroll). A
    // fixed-rows textarea scrolls internally, which lets the mention mirror
    // drift out of step with the (transparent) typed text — growing the box
    // keeps every line visible so mirror and textarea can't disagree. Also
    // re-sync the mirror's scroll for the capped-height case.
    useLayoutEffect(() => {
      const node = textareaRef.current;
      if (!node) return;
      if (!body) {
        // Empty draft: drop the inline height so the resting size comes from
        // rows/min-height alone. Never measure here — on mount (tab just
        // opened) the element can be mid-layout and scrollHeight reads tiny,
        // which would stick as a collapsed box.
        node.style.height = '';
        return;
      }
      node.style.height = 'auto';
      // +2 covers the top/bottom 1px borders (box-sizing: border-box). The
      // CSS min/max-height clamp bogus measurements either way.
      node.style.height = `${node.scrollHeight + 2}px`;
      if (highlightRef.current) {
        highlightRef.current.scrollTop = node.scrollTop;
      }
    }, [body]);

    // Emoji inserter: a curated popover that drops the picked emoji into the
    // textarea at the caret (opens above — the composer sits at the panel bottom).
    const [emojiOpen, setEmojiOpen] = useState(false);
    const emojiTriggerRef = useRef<HTMLButtonElement>(null);
    const { menuRef: emojiMenuRef, style: emojiMenuStyle } =
      useAnchoredDropdown(
        emojiOpen,
        emojiTriggerRef,
        () => setEmojiOpen(false),
        260,
        { width: EMOJI_PICKER_MENU_WIDTH, placement: 'above' },
      );

    const insertEmoji = (emoji: string) => {
      const el = textareaRef.current;
      const start = el?.selectionStart ?? body.length;
      const end = el?.selectionEnd ?? body.length;
      setBody(body.slice(0, start) + emoji + body.slice(end));
      setEmojiOpen(false);
      // Return focus to the textarea with the caret just after the emoji.
      requestAnimationFrame(() => {
        const node = textareaRef.current;
        if (!node) return;
        node.focus();
        const caret = start + emoji.length;
        node.setSelectionRange(caret, caret);
      });
    };

    const reset = () => {
      setBody('');
      setPinned(true);
    };

    const post = async () => {
      const trimmed = body.trim();
      if (!trimmed || isPosting) return;
      const videoTimeSeconds = pinned ? playheadTime() : null;
      // Captured before the composer is cleared, so the marks that go with this
      // body are the ones that were on screen when it was written (PLAT-11290).
      const strokes = annotations?.strokes ?? [];
      const drawing: Drawing | null = strokes.length
        ? { v: DRAWING_PAYLOAD_VERSION, strokes }
        : null;
      setIsPosting(true);
      // Clear the composer immediately — the store inserts the comment
      // optimistically, so the input shouldn't wait on the network write to
      // clear (that lag is what made the cleared textarea trail the posted
      // comment by a second or two).
      reset();
      // Same reasoning for the marks: clear the frame as the comment appears,
      // rather than leaving ink hanging over the video until the write lands.
      annotations?.reset();
      setDrawingOpen(false);
      // The editor holds display mentions (`@Name`); convert to storable
      // `@[Name](id)` tokens at post time. Picks come last so a teammate chosen
      // from the popover wins over the roster's by-name guess (two teammates
      // can share a display name).
      await store.addComment(
        encodeMentionTokens(trimmed, [...mentionPeople, ...mention.picks]),
        videoTimeSeconds,
        drawing,
      );
      setIsPosting(false);
      // On failure the store rolls back the optimistic comment and surfaces
      // `lastError`; restore the draft so it can be retried.
      if (store.lastError) {
        setBody(trimmed);
        setPinned(videoTimeSeconds !== null);
        // Put the marks back too — losing a drawing to a failed write would
        // cost more than losing the text, since it cannot be retyped.
        if (drawing) annotations?.setStrokes(drawing.strokes);
        videoCreator.showToast({
          state: 'error',
          message: 'Couldn’t post your comment. Please try again.',
        });
      }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter posts; Shift+Enter inserts a newline; Escape discards the draft
      // (mirrors the edit/reply composers in CommentList).
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void post();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        // Escape means "discard my draft", not "close the modal" — without
        // stopPropagation the modal's own Escape handler dismisses everything.
        e.stopPropagation();
        reset();
        e.currentTarget.blur();
      }
    };

    // The chip now sits inside the field, so the text has to start after it. Its
    // width varies with the label ("@ 0:48" vs "No timecode"), so it is measured
    // rather than guessed — a fixed indent would clip the chip or leave a gap.
    // `text-indent` moves only the FIRST line, so wrapped lines run the full
    // width, matching the reference.
    const chipRef = useRef<HTMLButtonElement>(null);
    const [chipWidth, setChipWidth] = useState(0);
    useLayoutEffect(() => {
      const node = chipRef.current;
      if (!node) return;
      const measure = () => setChipWidth(node.getBoundingClientRect().width);
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(node);
      return () => ro.disconnect();
    }, [pinned]);
    // Chip width plus the gap before the first character.
    const textIndent = chipWidth ? chipWidth + 6 : 0;

    const canPost = body.trim().length > 0 && !isPosting;

    return (
      <Container>
        <InputShell $highlight={!!annotations && drawingOpen}>
          {/* Inside the field, at the head of the first line — the timecode
              belongs to the note being written, so it reads as part of it rather
              than as a control parked in the row below. */}
          <Chip
            ref={chipRef}
            type="button"
            data-testid="comment-composer-timecode"
            $active={pinned}
            // Toggles between pinning to the live playhead and posting a
            // general (unpinned) comment.
            onClick={() => setPinned(p => !p)}
            aria-label={
              pinned
                ? 'Posting at this timecode — click to remove'
                : 'Posting without a timecode — click to pin'
            }
          >
            {pinned ? `@ ${convertTimeToMMSS(playheadTime())}` : 'No timecode'}
          </Chip>
          {/* Mirror of the textarea text with `@Name` mentions tinted green
              (--primary, the link token). The textarea above it renders its
              own text transparent (caret still visible), so the tinted mirror
              shows through — a plain <textarea> can't color substrings. */}
          <HighlightLayer
            ref={highlightRef}
            aria-hidden
            style={{ textIndent: `${textIndent}px` }}
          >
            {splitDisplayForRender(body, mentionPeople).map((seg, i) =>
              seg.type === 'mention' ? (
                <MentionText key={i}>@{seg.name}</MentionText>
              ) : (
                <React.Fragment key={i}>{seg.value}</React.Fragment>
              ),
            )}
            {/* Trailing newline keeps the mirror's scroll height in step with
                the textarea when the draft ends on an empty line. */}
            {'\n'}
          </HighlightLayer>
          <TextArea
            ref={textareaRef}
            data-testid="comment-composer-input"
            // Same indent on both, or the tinted mention mirror drifts out of
            // alignment with the (transparent) typed text.
            style={{ textIndent: `${textIndent}px` }}
            value={body}
            // Short enough to stay on one line beside the timecode chip: the
            // field is ~365px, and the chip plus its indent takes ~68px of it,
            // so the longer wording wrapped. Keeps the @-mention hint, which is
            // a nudge rather than the only route — typing @ opens the
            // autocomplete regardless.
            placeholder={
              drawingOpen
                ? 'Describe what you marked…'
                : 'Comment, or @ to mention'
            }
            onChange={mention.handleChange}
            onScroll={e => {
              if (highlightRef.current) {
                highlightRef.current.scrollTop = e.currentTarget.scrollTop;
              }
            }}
            onKeyDown={e => {
              // The mention popover consumes nav/select/close keys first.
              if (mention.handleKeyDown(e)) return;
              onKeyDown(e);
            }}
            rows={2}
          />
        </InputShell>
        <MentionSuggestions
          open={mention.open}
          results={mention.results}
          activeIndex={mention.activeIndex}
          setActiveIndex={mention.setActiveIndex}
          onSelect={mention.select}
          menuRef={mention.menuRef}
          menuStyle={mention.menuStyle}
          colors={personColors}
        />
        {/* PLAT-11289 — drawing tools, on their own row so they don't crowd the
            emoji / timecode / Send row below. Absent unless the host passes an
            annotations controller, i.e. unless the flag is on. */}
        <Footer>
          {/* PLAT-11289 — one row, two states. Drawing needs the whole line, so
              opening it takes over the emoji trigger and the timecode chip
              rather than adding a second row. The back chevron inside the
              controls is the way out. */}
          {annotations && drawingOpen ? (
            <AnnotationControls
              annotations={annotations}
              onExit={() => {
                setDrawingOpen(false);
                // Hand the canvas back: a tool left armed would keep the
                // overlay swallowing clicks meant for the video. Marks already
                // drawn stay, so they can still be posted.
                annotations.setTool(null);
              }}
            />
          ) : (
            <FooterLeft>
              {/* Drawing comes first — it is the affordance reviewers reach for,
                and it is the one that takes over the row. */}
              {annotations && (
                <ComposerTool
                  type="button"
                  aria-label="Draw an annotation"
                  aria-pressed={false}
                  data-testid="comment-composer-draw"
                  data-tooltip-id="comment-composer-emoji-tooltip"
                  data-tooltip-content="Draw an annotation"
                  onClick={() => {
                    setDrawingOpen(true);
                    // Arm freehand so the first drag draws instead of doing
                    // nothing.
                    annotations.setTool('freehand');
                    // Never start drawing into a hidden canvas: if the reviewer
                    // left drawings hidden, opening the tools brings them back.
                    annotations.setDrawingsVisible(true);
                    // Point the reviewer at the field the note goes in, so the
                    // marks do not sit there un-described. Guarded per
                    // CLAUDE.md pitfall 10: taking focus while the renderer
                    // iframe holds it fires the deactivation reaction, which
                    // can revert an uncommitted canvas edit. Clicking this
                    // button means the iframe is not active, so the guard
                    // passes here — it is insurance against a future caller.
                    if (document.activeElement instanceof HTMLIFrameElement) {
                      return;
                    }
                    textareaRef.current?.focus();
                  }}
                >
                  <DrawFreehandIcon
                    width="18"
                    height="18"
                    strokeColor="currentColor"
                  />
                </ComposerTool>
              )}
              {/* Emoji inserter: opens a curated picker; picking drops the emoji
                into the textarea at the caret. */}
              <ComposerTool
                ref={emojiTriggerRef}
                type="button"
                aria-label="Add emoji"
                aria-haspopup="menu"
                aria-expanded={emojiOpen}
                data-testid="comment-composer-emoji"
                data-tooltip-id="comment-composer-emoji-tooltip"
                data-tooltip-content="Add emoji"
                onClick={() => setEmojiOpen(o => !o)}
              >
                <EmojiIcon width="18" height="18" strokeColor="currentColor" />
              </ComposerTool>
            </FooterLeft>
          )}
          {/* Icon-only, so the row still fits once the drawing controls take it
              over. The label and tooltip carry the meaning, and the busy state
              is the disabled + dimmed icon rather than a text swap. */}
          <PostButton
            type="button"
            data-testid="comment-composer-post"
            disabled={!canPost}
            aria-label={isPosting ? 'Sending…' : 'Send'}
            aria-busy={isPosting}
            data-tooltip-id="comment-composer-emoji-tooltip"
            data-tooltip-content={isPosting ? 'Sending…' : 'Send'}
            onClick={() => void post()}
          >
            {/* Heavier stroke than the icon's default: at 16px on a filled
                green button a hairline reads as washed out. */}
            <SendIcon
              width="16"
              height="17"
              strokeColor="currentColor"
              strokeWidth="1.8"
            />
          </PostButton>
        </Footer>
        {emojiOpen &&
          emojiMenuStyle &&
          ReactDOM.createPortal(
            <EmojiPickerMenu
              ref={emojiMenuRef}
              style={emojiMenuStyle}
              testId="comment-composer-emoji-picker"
              optionTestId="comment-composer-emoji-option"
              labelFor={emoji => `Insert ${emoji}`}
              onPick={insertEmoji}
            />,
            document.body,
          )}
        {/* The timecode chip has no tooltip: inside the field it is legible on
            its own, and a tooltip over the text being typed is in the way. Its
            aria-label still carries the toggle's meaning. */}
        {ReactDOM.createPortal(
          <Tooltip
            id="comment-composer-emoji-tooltip"
            style={COMPACT_TOOLTIP_STYLE}
            place="top"
            positionStrategy="fixed"
          />,
          document.body,
        )}
      </Container>
    );
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm, 8px);
  flex-shrink: 0;
`;

// Wraps the textarea + its mention-highlight mirror. Carries the input fill so
// the textarea itself can be transparent (letting the mirror's tinted text show
// through).
const InputShell = styled.div<{ $highlight?: boolean }>`
  position: relative;
  border-radius: var(--radius-sm, 6px);
  /* Subtle gray resting fill (one step above the panel --background), matching
     the shared common/Textarea + Input primitives. */
  background: var(--input-background);
  /* While drawing is open, ring the field in gold so it is obvious the marks
     still need a note. Gold is the selected/visual token — the same one the
     armed tool and the open toggle use — so the mode reads as one thing rather
     than three unrelated highlights. An inset ring rather than a border: a
     border would change the box height and shift the composer. */
  box-shadow: ${props =>
    props.$highlight ? 'inset 0 0 0 1px var(--accent)' : 'none'};
  transition: box-shadow var(--transition-fast, 0.15s) ease;
`;

// Metrics shared by the textarea and its mirror — any drift between the two
// mis-aligns the tinted mentions under the (transparent) typed text.
const inputMetrics = `
  box-sizing: border-box;
  padding: var(--spacing-sm, 8px);
  border-width: 1px;
  border-style: solid;
  font-family: var(--font-primary, Inter, sans-serif);
  font-size: var(--text-sm, 14px);
  line-height: 1.5;
`;

// Auto-grow ceiling: the resting 2 rows plus ONE extra line, then the textarea
// scrolls internally (mirror synced via the onScroll handler + the auto-grow
// effect). 3 lines × 21px (14px × 1.5 line-height) + 16px padding + 2px border.
const COMPOSER_MAX_HEIGHT = '81px';

// The scroll-synced mirror behind the textarea: same box + font metrics,
// transparent border (the visible border lives on the textarea), no pointer
// events. Wrapping must match the textarea's (pre-wrap + break-word).
const HighlightLayer = styled.div`
  ${inputMetrics}
  position: absolute;
  inset: 0;
  border-color: transparent;
  overflow: hidden;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  color: var(--foreground);
  pointer-events: none;
`;

// A display mention (`@Name`) in the mirror — gold accent, regular weight,
// matching the rendered MentionChip in the comment list.
const MentionText = styled.span`
  color: var(--accent);
`;

const TextArea = styled.textarea`
  ${inputMetrics}
  position: relative;
  /* Block, not the textarea default inline-block: inline layout leaves a few
     px of baseline/descender space under the field, and the shell's gray fill
     shows through that gap as a stray strip below the border. */
  display: block;
  width: 100%;
  /* Height is driven by the auto-grow effect; manual resize would fight it
     (and let the mention mirror drift out of alignment). min/max clamp the
     inline height between the resting 2 rows (2 × 21px + 16px padding + 2px
     border) and one extra line — so a bogus scrollHeight measurement (e.g.
     mid-layout while the tab is opening) can never collapse the field. */
  resize: none;
  min-height: 60px;
  max-height: ${COMPOSER_MAX_HEIGHT};
  overflow-y: auto;
  border-color: var(--border);
  border-radius: var(--radius-sm, 6px);
  /* Transparent so the mirror's tinted text shows through; the caret keeps
     the normal foreground color. */
  background: transparent;
  color: transparent;
  caret-color: var(--foreground);
  transition: border-color var(--transition-fast, 0.15s);

  &:hover {
    border-color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-sm, 8px);
`;

// Left cluster of composer tools (the emoji inserter).
const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-2xs, 4px);
`;

// Ghost icon button for a composer tool; stays highlighted while its popover is
// open (aria-expanded).
const ComposerTool = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: none;
  border-radius: var(--radius-sm, 6px);
  /* Gold while drawing mode is open — the design system's selected state. */
  color: ${props => (props.$active ? 'var(--accent)' : 'var(--text-muted)')};
  background: ${props =>
    props.$active
      ? 'color-mix(in srgb, var(--accent) 18%, transparent)'
      : 'none'};
  cursor: pointer;
  transition:
    color var(--transition-fast, 0.15s) ease,
    background var(--transition-fast, 0.15s) ease;

  &:hover,
  &[aria-expanded='true'] {
    color: var(--foreground);
    background: var(--surface-elevated);
  }
`;

// Timecode toggle, sharing the comment list's restrained chip look. Pinned
// (active) fills as a --surface-elevated pill with brighter text; unpinned is a
// muted, fill-less pill. The "@ mm:ss" vs "No timecode" label also conveys the
// state. No gold — gold is reserved for visual controls per the design system.
const Chip = styled.button<{ $active: boolean }>`
  /* Overlaid on the field's first line rather than sitting in the text flow: a
     textarea cannot hold inline content, so the chip is positioned here and the
     text is pushed past it with text-indent.
   
     left matches the shared input padding (8px) plus its 1px border, so the
     chip starts where the text would. top is that same 9px plus half the
     slack in the line box: the first line is 21px tall (14px x 1.5) and the chip
     is 16px, so centering it costs 2.5px. Rounded to 12px, which reads level
     with the text — at 9px it sat visibly high.
   
     The explicit line-height is what makes that arithmetic hold: without it the
     chip inherits normal, which varies by font and would drift the alignment. */
  position: absolute;
  top: 12px;
  left: 9px;
  line-height: 14px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  padding: 1px var(--spacing-xs, 6px);
  border: none;
  border-radius: var(--radius-sm, 6px);
  background: ${props =>
    props.$active ? 'var(--surface-elevated)' : 'transparent'};
  color: ${props =>
    props.$active ? 'var(--text-secondary)' : 'var(--text-muted)'};
  font-size: var(--text-xs, 12px);
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition:
    color var(--transition-fast, 0.15s) ease,
    background var(--transition-fast, 0.15s) ease;

  &:hover {
    color: var(--foreground);
  }
`;

const PostButton = styled.button`
  /* Pinned to the right; the tools + timecode chip sit at the left. */
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  /* Square: icon-only, so width tracks height instead of a text label. */
  width: var(--btn-h-sm, 32px);
  height: var(--btn-h-sm, 32px);
  padding: 0;
  border: none;
  border-radius: var(--radius-sm, 6px);
  background: var(--primary);
  color: var(--background);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
